// ---------------------------------------------------------------------------
// Discovery URL Filters Hook
// ---------------------------------------------------------------------------
// URL-based filter state management following the period pattern.
// URL is the single source of truth (like useExplorerUrlState for period).
// All derived state computed via useMemo (chips, API params).
// ---------------------------------------------------------------------------

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DiscoveryFilter, ApiFilterParams } from '@/lib/discovery/types';
import type { DataSourceId } from '@/components/v11/discovery/types';
import type { DimensionConfig } from '@/components/v11/funnel/constants';
import type { FilterChip } from '@/components/v11/discovery/types';
import { filtersToSearchParams, searchParamsToFilters } from '@/lib/discovery/filterUrlCodec';
import { filterStateToChips } from '@/lib/discovery/filterStateToChips';
import { filtersToApiParams } from '@/lib/discovery/filtersToApiParams';
import { retainApplicableFilters } from '@/lib/discovery/retainApplicableFilters';

/**
 * Options for useDiscoveryUrlFilters hook.
 */
interface UseDiscoveryUrlFiltersOptions {
  readonly dataSource: DataSourceId;
  readonly periodDates: {
    readonly date_from?: string;
    readonly date_to?: string;
  };
  readonly drillDownFilter?: Record<string, string | number>;
  readonly searchTerm?: string;
  readonly accountId?: number;
}

/**
 * Return type for useDiscoveryUrlFilters hook.
 */
interface UseDiscoveryUrlFiltersResult {
  /** Current filter state (from URL) */
  readonly filters: readonly DiscoveryFilter[];
  /** Filter chips for toolbar display (derived) */
  readonly filterChips: readonly FilterChip[];
  /** Complete API params: filters + period + search + drillDown (derived) */
  readonly apiFilters: ApiFilterParams;
  /** Whether any filters are active */
  readonly hasActiveFilters: boolean;
  /** Whether own channels are included (default: false) */
  readonly includeOwnChannels: boolean;
  /** Toggle include own channels on/off */
  readonly setIncludeOwnChannels: (value: boolean) => void;
  /** Add a new filter dimension */
  readonly addFilter: (
    dimension: DimensionConfig,
    values: readonly (string | number)[],
    valueLabels: readonly string[],
    valuePlatforms?: readonly string[]
  ) => void;
  /** Remove a specific filter by chip ID */
  readonly removeFilter: (chipId: string) => void;
  /** Clear all filters */
  readonly clearAll: () => void;
  /** Bulk-replace all filters (for persona loading) */
  readonly loadFilters: (newFilters: readonly DiscoveryFilter[]) => void;
  /** Toggle a filter dimension on/off by chip ID */
  readonly toggleFilter: (chipId: string) => void;
  /** Retain applicable filters when datasource changes */
  readonly retainApplicableOnSourceChange: (newDataSource: DataSourceId) => void;
}

/**
 * Discovery filter state management with URL persistence.
 *
 * Follows the same pattern as period system:
 * - URL is the single source of truth
 * - Immutable URL updates via setSearchParams(prev => new URLSearchParams(prev))
 * - All display state derived via useMemo (chips, API params)
 *
 * @param options - Datasource, period dates, drill-down, search term
 * @returns Filter state, chips, API params, and action handlers
 */
export function useDiscoveryUrlFilters({
  dataSource,
  periodDates,
  drillDownFilter,
  searchTerm,
  accountId,
}: UseDiscoveryUrlFiltersOptions): UseDiscoveryUrlFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // -------------------------------------------------------------------------
  // State (URL is the single source of truth)
  // -------------------------------------------------------------------------

  const filters = useMemo(() => searchParamsToFilters(searchParams), [searchParams]);

  // Include Own Channels toggle (URL param: ioc=1 means true, absent/0 means false)
  const includeOwnChannels = searchParams.get('ioc') === '1';

  const setIncludeOwnChannels = useCallback(
    (value: boolean) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set('ioc', '1');
        } else {
          next.delete('ioc');
        }
        return next;
      });
    },
    [setSearchParams]
  );

  // -------------------------------------------------------------------------
  // Derived State (no manual sync needed)
  // -------------------------------------------------------------------------

  // Derive filter chips from URL filters (always in sync)
  const filterChips = useMemo(() => filterStateToChips(filters), [filters]);

  // Derive API parameters from URL filters + context (always correct)
  // Only include enabled filters in API params
  const apiFilters = useMemo(() => {
    // Filter out fully-disabled dimensions and strip individually-disabled values
    const enabledFilters = filters
      .filter(f => f.enabled !== false)
      .map(f => {
        if (!f.disabledValues || f.disabledValues.size === 0) return f;
        // Remove individually disabled values
        const activeValues: (string | number)[] = [];
        const activeLabels: string[] = [];
        for (let i = 0; i < f.values.length; i++) {
          if (!f.disabledValues.has(String(f.values[i]))) {
            activeValues.push(f.values[i]);
            activeLabels.push(f.valueLabels[i] || String(f.values[i]));
          }
        }
        if (activeValues.length === 0) return null;
        return { ...f, values: activeValues, valueLabels: activeLabels };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);
    const filterParams = filtersToApiParams(enabledFilters, dataSource);

    // Merge with period dates (like period system)
    let combined: ApiFilterParams = { ...filterParams, ...periodDates };

    // Add search term if provided
    if (searchTerm) {
      combined = { ...combined, search: searchTerm };
    }

    // Add drill-down filter if provided
    if (drillDownFilter) {
      combined = { ...combined, ...drillDownFilter };
    }

    // Add account_id for multi-tenant isolation
    if (accountId) {
      combined = { ...combined, account_id: accountId };
    }

    // Include own channels toggle (profiles and comments in Discovery)
    if (dataSource === 'profiles' || dataSource === 'comments') {
      combined = { ...combined, include_own_channels: includeOwnChannels ? 'true' : 'false' };
    }

    return combined;
  }, [filters, dataSource, periodDates, searchTerm, drillDownFilter, accountId, includeOwnChannels]);

  const hasActiveFilters = filters.length > 0;

  // -------------------------------------------------------------------------
  // Actions (all update URL immutably)
  // -------------------------------------------------------------------------

  /**
   * Add a new filter dimension with selected values.
   * Updates URL params, which triggers filter state update.
   *
   * If dimension already exists, replaces it (no append).
   */
  const addFilter = useCallback(
    (
      dimension: DimensionConfig,
      values: readonly (string | number)[],
      valueLabels: readonly string[],
      valuePlatforms?: readonly string[]
    ) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev); // Immutable

        // Remove existing filter for this dimension (replace, not append)
        next.delete(`f_${dimension.key}`);
        next.delete(`f_${dimension.key}_labels`);
        next.delete(`f_${dimension.key}_platforms`);

        // Set new values
        if (values.length > 0) {
          next.set(`f_${dimension.key}`, values.join(','));
          if (valueLabels.length > 0) {
            next.set(`f_${dimension.key}_labels`, valueLabels.join('|'));
          }
          if (valuePlatforms && valuePlatforms.length > 0) {
            next.set(`f_${dimension.key}_platforms`, valuePlatforms.join('|'));
          }
        }

        return next;
      });
    },
    [setSearchParams]
  );

  /**
   * Remove a specific filter value by chip ID.
   * Chip ID format: "{dimension}-{value}" (e.g., "gender-2", "verified-yes")
   *
   * If removing the last value of a dimension, removes the entire dimension.
   */
  const removeFilter = useCallback(
    (chipId: string) => {
      // Parse chip ID: "{dimension}-{value}" or "{dimension}-range" for sentiment
      const dashIndex = chipId.lastIndexOf('-');
      if (dashIndex === -1) return;

      const dimension = chipId.slice(0, dashIndex);
      const valueToRemove = chipId.slice(dashIndex + 1);

      // Special case: sentiment range
      if (valueToRemove === 'range') {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete(`f_${dimension}`);
          next.delete(`f_${dimension}_labels`);
          next.delete(`f_${dimension}_platforms`);
          return next;
        });
        return;
      }

      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const currentFilters = searchParamsToFilters(prev);
        const targetFilter = currentFilters.find(f => f.dimension === dimension);

        if (!targetFilter) return next;

        // Remove the specific value
        const removeIndices = new Set(
          targetFilter.values
            .map((v, idx) => String(v) === valueToRemove ? idx : -1)
            .filter(idx => idx >= 0)
        );
        const newValues = targetFilter.values.filter((_, idx) => !removeIndices.has(idx));
        const newLabels = targetFilter.valueLabels.filter((_, idx) => !removeIndices.has(idx));
        const newPlatforms = targetFilter.valuePlatforms?.filter((_, idx) => !removeIndices.has(idx));

        // Remove params
        next.delete(`f_${dimension}`);
        next.delete(`f_${dimension}_labels`);
        next.delete(`f_${dimension}_platforms`);

        // Re-add if there are remaining values
        if (newValues.length > 0) {
          next.set(`f_${dimension}`, newValues.join(','));
          if (newLabels.length > 0) {
            next.set(`f_${dimension}_labels`, newLabels.join('|'));
          }
          if (newPlatforms && newPlatforms.length > 0) {
            next.set(`f_${dimension}_platforms`, newPlatforms.join('|'));
          }
        }

        // Clean removed chip from fdv
        const fdvRaw = next.get('fdv');
        if (fdvRaw) {
          const fdvSet = new Set(fdvRaw.split('~'));
          fdvSet.delete(chipId);
          if (fdvSet.size > 0) {
            next.set('fdv', Array.from(fdvSet).join('~'));
          } else {
            next.delete('fdv');
          }
        }

        return next;
      });
    },
    [setSearchParams]
  );

  /**
   * Clear all filter params from URL.
   * Removes all f_* params.
   */
  const clearAll = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      // Find and remove all filter params (f_* prefix) and fd (disabled state)
      const keysToDelete = Array.from(next.keys()).filter(k => k.startsWith('f_'));
      for (const key of keysToDelete) {
        next.delete(key);
      }
      next.delete('fd');
      next.delete('fdv');
      return next;
    });
  }, [setSearchParams]);

  /**
   * Bulk-replace all filters at once (for persona loading).
   * Clears all f_* params then sets new ones atomically.
   */
  const loadFilters = useCallback(
    (newFilters: readonly DiscoveryFilter[]) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);

        // Clear all current filter params
        const keysToDelete = Array.from(next.keys()).filter(k => k.startsWith('f_'));
        for (const key of keysToDelete) {
          next.delete(key);
        }

        // Set new filters
        const newParams = filtersToSearchParams(newFilters);
        for (const [key, value] of Object.entries(newParams)) {
          next.set(key, value);
        }

        return next;
      });
    },
    [setSearchParams]
  );

  /**
   * Toggle a single filter value on/off by chip ID.
   * Uses fdv (filter disabled values) URL param to track individual chip IDs.
   * Chip ID format: "{dimension}-{value}" (e.g., "platform-1", "gender-2")
   */
  const toggleFilter = useCallback(
    (chipId: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const disabledRaw = next.get('fdv');
        const disabledSet = new Set(disabledRaw ? disabledRaw.split('~') : []);

        if (disabledSet.has(chipId)) {
          disabledSet.delete(chipId);
        } else {
          disabledSet.add(chipId);
        }

        if (disabledSet.size > 0) {
          next.set('fdv', Array.from(disabledSet).join('~'));
        } else {
          next.delete('fdv');
        }

        // Clean up legacy fd param
        next.delete('fd');

        return next;
      });
    },
    [setSearchParams]
  );

  /**
   * Retain only applicable filters when datasource changes.
   * Updates URL to remove inapplicable dimensions.
   *
   * Example: Switching from Profiles to Comments drops Industry filter
   * but keeps Gender filter (remapped to author_gender).
   */
  const retainApplicableOnSourceChange = useCallback(
    (newDataSource: DataSourceId) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);

        // Read current filters from URL (not closure) to avoid stale state
        const currentFilters = searchParamsToFilters(prev);
        const applicable = retainApplicableFilters(currentFilters, newDataSource);

        // Clear all current filter params
        const keysToDelete = Array.from(next.keys()).filter(k => k.startsWith('f_'));
        for (const key of keysToDelete) {
          next.delete(key);
        }

        // Re-add only applicable filters
        const newParams = filtersToSearchParams(applicable);
        for (const [key, value] of Object.entries(newParams)) {
          next.set(key, value);
        }

        return next;
      });
    },
    [setSearchParams]
  );

  return {
    filters,
    filterChips,
    apiFilters,
    hasActiveFilters,
    includeOwnChannels,
    setIncludeOwnChannels,
    addFilter,
    removeFilter,
    toggleFilter,
    clearAll,
    loadFilters,
    retainApplicableOnSourceChange,
  };
}
